import { useEffect, useState } from 'react';
import { Modal, Button, Input } from '@react-apps/design-system';
import type {
  BillingAgreement,
  SkippedGatewayChargeReason,
  SyncChargeHistoryResult,
} from '../../domain/entities/sales.entity';
import {
  useCancelBillingAgreement,
  useCancelDebtArrangement,
  useRevertDebtArrangement,
  useAbandonContract,
  useResolveBillingAgreementCycleCount,
  useSyncBillingAgreementChargeHistory,
  useSyncContractChargeHistory,
} from '../hooks/use-sales-actions';
import {
  BillingAgreementStatusBadge,
  ErrorBanner,
  InfoNotice,
  SuccessBanner,
  getApiErrorMessage,
  getGatewayActionErrorMessage,
} from '../shared/display';

// --- Cancelar Billing Agreement / Acordo de Cobrança ---

export function CancelBillingAgreementModal({
  title,
  invalidateKey,
  billingAgreement,
  open,
  onClose,
}: {
  title: string;
  invalidateKey: readonly unknown[];
  billingAgreement: BillingAgreement;
  open: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const mutation = useCancelBillingAgreement(invalidateKey);

  useEffect(() => {
    if (open) {
      setReason('');
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const result = mutation.data;
  const done = mutation.isSuccess;

  const handleConfirm = () => {
    mutation.mutate({
      billingAgreementId: billingAgreement.id,
      body: { reason: reason || undefined },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description="Você tem certeza que deseja enviar um pedido de cancelamento ao gateway de pagamento?"
      size="md"
      footer={
        done ? (
          <Button onClick={onClose}>Fechar</Button>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Voltar
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirm}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Solicitando…' : 'Confirmar cancelamento'}
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {done && result?.outcome === 'CANCEL_REQUESTED' && (
          <SuccessBanner message="Pedido enviado. Assim que o provedor confirmar o cancelamento, o status é atualizado automaticamente." />
        )}
        {done && result?.outcome === 'MANUAL_CANCELLATION_REQUIRED' && (
          <div className="space-y-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            <p>
              Este provedor não aceita cancelamento pela API. Cancele
              manualmente direto no painel dele.
            </p>
            {result.subscriptionCode && (
              <p>
                Código da assinatura:{' '}
                <span className="font-mono">{result.subscriptionCode}</span>
              </p>
            )}
          </div>
        )}
        {mutation.isError && (
          <ErrorBanner
            message={getGatewayActionErrorMessage(
              mutation.error,
              'Falha ao solicitar o cancelamento.',
            )}
          />
        )}
        {!done && (
          <>
            <InfoNotice>
              O cancelamento só passa a valer quando o gateway de pagamentos
              confirma por webhook. Até lá, o status permanece o mesmo.
            </InfoNotice>
            <Input
              label="Motivo do cancelamento (opcional)"
              id="ba-cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: cliente solicitou reembolso"
            />
          </>
        )}
      </div>
    </Modal>
  );
}

// --- Resolver total de ciclos da assinatura pela API do gateway (T2) ---

/**
 * Dispara à mão o mesmo passo que a ingestão de webhook tenta a cada evento de
 * uma assinatura sem total de ciclos conhecido. Dois usos:
 *
 * - **Operação:** desempatar uma BA que ficou sem ciclos porque o gateway estava
 *   fora do ar quando o webhook chegou.
 * - **Validação:** conferir o valor que o gateway devolve para uma assinatura
 *   real (Guru/Hotmart) contra o que se espera do plano — por isso o desfecho é
 *   mostrado com o número cru, não só um "pronto".
 */
export function ResolveCycleCountModal({
  invalidateKey,
  billingAgreement,
  open,
  onClose,
}: {
  invalidateKey: readonly unknown[];
  billingAgreement: BillingAgreement;
  open: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const mutation = useResolveBillingAgreementCycleCount(invalidateKey);

  useEffect(() => {
    if (open) {
      setReason('');
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const result = mutation.data;
  const done = mutation.isSuccess;

  const handleConfirm = () => {
    mutation.mutate({
      billingAgreementId: billingAgreement.id,
      body: { reason: reason || undefined },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Resolver total de ciclos"
      description="Consulta a API do gateway para descobrir quantas cobranças a assinatura tem contratadas."
      size="md"
      footer={
        done ? (
          <Button onClick={onClose}>Fechar</Button>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Voltar
            </Button>
            <Button onClick={handleConfirm} disabled={mutation.isPending}>
              {mutation.isPending ? 'Consultando…' : 'Consultar gateway'}
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {done && result?.outcome === 'marked_open' && (
          <SuccessBanner
            message={`Gateway confirmou assinatura sem fim (ciclos = 0). Ciclo atual: ${result.currentCycleIndex}.`}
          />
        )}
        {done && result?.outcome === 'marked_fixed' && (
          <SuccessBanner
            message={`Gateway devolveu ${result.resolvedCycles} ciclos (ciclo atual: ${result.currentCycleIndex}). A BA passou a "assinatura com ciclos fixos"${
              result.createdInvoiceCycleIndexes.length > 0
                ? ` e ${result.createdInvoiceCycleIndexes.length} invoice(s) futura(s) foram criadas (ciclos ${result.createdInvoiceCycleIndexes.join(', ')})`
                : ''
            }.`}
          />
        )}
        {done && result?.outcome === 'anomaly_ignored' && (
          <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            O gateway devolveu {result.resolvedCycles} ciclos, mas a assinatura
            já está no ciclo {result.currentCycleIndex}. Nada foi alterado — o
            caso precisa ser investigado antes.
          </div>
        )}
        {done && result?.outcome === 'skipped_terminal_status' && (
          <InfoNotice>
            <span className="inline-flex flex-wrap items-center gap-1">
              Esta assinatura já está encerrada (
              <BillingAgreementStatusBadge status={billingAgreement.status} />)
              — não haverá mais cobrança, então não há total de ciclos a
              resolver. O gateway não foi consultado e nada foi alterado.
            </span>
          </InfoNotice>
        )}
        {/* Não é erro: o gateway respondeu, dizendo que assinatura não existe. */}
        {done && result?.outcome === 'gateway_has_no_subscription' && (
          <InfoNotice>
            O gateway respondeu que o código{' '}
            <span className="font-mono">{billingAgreement.externalId}</span> não
            é uma assinatura — provavelmente esta venda é um{' '}
            <strong>parcelamento inteligente</strong> (Smart Installment da
            Hotmart): cobra em parcelas, mas não existe como assinatura lá,
            então não há total de ciclos a resolver. Nada foi alterado.
          </InfoNotice>
        )}
        {mutation.isError && (
          <ErrorBanner
            message={getGatewayActionErrorMessage(
              mutation.error,
              'Falha ao consultar o gateway.',
            )}
          />
        )}
        {!done && (
          <>
            <InfoNotice>
              É o mesmo passo que a ingestão de webhook tenta automaticamente a
              cada evento desta assinatura. Se o gateway devolver um número de
              ciclos, a BA passa a <strong>ciclos fixos</strong> e as invoices
              dos ciclos futuros são criadas vazias.
            </InfoNotice>
            <Input
              label="Motivo (opcional)"
              id="ba-resolve-cycles-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: validação contra assinatura real em sandbox"
            />
          </>
        )}
      </div>
    </Modal>
  );
}

// --- Sincronizar cobranças que nunca chegaram por webhook ---

/** O que o gateway cobrou e ainda assim não virou fatura aqui. */
const SKIPPED_REASON_LABELS: Record<SkippedGatewayChargeReason, string> = {
  unidentified: 'sem id no gateway',
  no_cycle_number: 'sem número de ciclo',
  unreadable_status: 'status do gateway que ainda não sabemos ler',
  incomplete: 'sem valor ou sem data',
  cycle_already_charged: 'ciclo que o gateway já cobrou por outra cobrança',
  period_already_charged: 'período que o gateway já cobrou por outra cobrança',
};

/**
 * Alvo da sincronização. A da BA é a unitária; a do contrato roda a mesma coisa
 * nas BAs de assinatura dele, e é o check local de cada uma que escolhe quais
 * de fato consultam o gateway.
 */
export type SyncChargeHistoryTarget =
  | { kind: 'billingAgreement'; billingAgreementId: string }
  | { kind: 'contract'; contractId: string };

/**
 * Traz do gateway as cobranças que nunca chegaram por webhook — a assinatura já
 * cobrava quando a conta foi conectada, ou a mensagem se perdeu — e cria aqui a
 * fatura paga de cada uma.
 *
 * O botão que abre isto só existe quando o servidor diz que há o que buscar
 * (`needsChargeHistorySync`), mas o recheque roda de novo **no clique**: entre a
 * tela ser desenhada e o operador decidir, o gatilho automático da ingestão pode
 * já ter consertado a BA — daí `no_sync_needed` ser desfecho comum aqui, e não
 * falha.
 *
 * **Sincronizar nem sempre cria fatura.** Quando a cobrança já estava aqui com
 * a identidade que o servidor inventou, o que muda é o id da transação, que
 * passa a ser o que se encontra no painel do gateway — é o
 * `substitutes_promoted`, e sem dizê-lo a tela pareceria não ter feito nada.
 */
export function SyncChargeHistoryModal({
  invalidateKey,
  target,
  open,
  onClose,
}: {
  invalidateKey: readonly unknown[];
  target: SyncChargeHistoryTarget;
  open: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const billingAgreementMutation =
    useSyncBillingAgreementChargeHistory(invalidateKey);
  const contractMutation = useSyncContractChargeHistory(invalidateKey);
  const mutation =
    target.kind === 'contract' ? contractMutation : billingAgreementMutation;

  useEffect(() => {
    if (open) {
      setReason('');
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirm = () => {
    const body = { reason: reason || undefined };
    if (target.kind === 'contract') {
      contractMutation.mutate({ contractId: target.contractId, body });
      return;
    }
    billingAgreementMutation.mutate({
      billingAgreementId: target.billingAgreementId,
      body,
    });
  };

  // Uma lista nos dois casos: o contrato devolve uma linha por BA, a BA devolve
  // a sua — assim o resumo abaixo não ramifica por alvo.
  const results: SyncChargeHistoryResult[] =
    target.kind === 'contract'
      ? (contractMutation.data?.billingAgreements ?? [])
      : billingAgreementMutation.data
        ? [billingAgreementMutation.data]
        : [];
  const done = mutation.isSuccess;
  const backfilledCount = results.reduce(
    (total, result) => total + result.backfilledInvoiceIds.length,
    0,
  );
  const skipped = results.flatMap((result) => result.skipped);
  const promotedCount = results.filter(
    (result) => result.outcome === 'substitutes_promoted',
  ).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sincronizar cobranças"
      description="Traz para cá as cobranças que o gateway teve e nunca chegaram por webhook — pagas, atrasadas ou estornadas."
      size="md"
      footer={
        done ? (
          <Button onClick={onClose}>Fechar</Button>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Voltar
            </Button>
            <Button onClick={handleConfirm} disabled={mutation.isPending}>
              {mutation.isPending ? 'Sincronizando…' : 'Sincronizar'}
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {!done && (
          <>
            <InfoNotice>
              O gateway é a fonte da verdade sobre o que ele cobrou, e cada
              cobrança entra com o estado que ele reporta. O que já foi pago
              aqui não é rebaixado: se ele trouxer um pagamento para um ciclo
              que já teve baixa manual, os dois ficam registrados e o caso é
              aberto como pagamento duplicado, para alguém decidir o estorno.
            </InfoNotice>
            <Input
              label="Motivo da sincronização (opcional)"
              id="sync-charge-history-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: contrato com o 1º ciclo faltando"
            />
          </>
        )}
        {done && backfilledCount > 0 && (
          <SuccessBanner
            message={
              backfilledCount === 1
                ? '1 cobrança do gateway registrada — a fatura dela já aparece na tabela, com o estado que o gateway reporta.'
                : `${backfilledCount} cobranças do gateway registradas — as faturas delas já aparecem na tabela, com o estado que o gateway reporta.`
            }
          />
        )}
        {done && backfilledCount === 0 && promotedCount > 0 && (
          <SuccessBanner
            message={
              promotedCount === 1
                ? 'Nenhuma cobrança nova: a que faltava já estava aqui e passou a carregar o id de transação do gateway — é por ele que se encontra a cobrança no painel do provedor.'
                : `Nenhuma cobrança nova: ${promotedCount} assinaturas tiveram transações que já estavam aqui trocando o id provisório pelo do gateway.`
            }
          />
        )}
        {/* Não é erro: o histórico local estava completo quando o check rodou. */}
        {done &&
          backfilledCount === 0 &&
          promotedCount === 0 &&
          skipped.length === 0 && (
            <InfoNotice>
              Nada a sincronizar — nenhum ciclo cobrado está faltando agora. O
              gatilho automático da ingestão pode ter resolvido esta assinatura
              entre a tela ser carregada e o clique.
            </InfoNotice>
          )}
        {done && skipped.length > 0 && (
          <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            <p>
              {skipped.length === 1
                ? '1 cobrança do gateway não foi reconstruída:'
                : `${skipped.length} cobranças do gateway não foram reconstruídas:`}
            </p>
            <ul className="mt-1 list-inside list-disc">
              {skipped.map((charge, index) => (
                <li key={`${charge.externalId ?? 'sem-id'}-${index}`}>
                  {charge.gatewayCycleNumber !== null
                    ? `ciclo ${charge.gatewayCycleNumber}`
                    : 'ciclo desconhecido'}
                  {charge.externalId ? ` (${charge.externalId})` : ''} —{' '}
                  {SKIPPED_REASON_LABELS[charge.reason] ?? charge.reason}
                  {/* O status cru é o que o dev precisa para mapeá-lo. */}
                  {charge.gatewayStatus ? `: ${charge.gatewayStatus}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        {mutation.isError && (
          <ErrorBanner
            message={getGatewayActionErrorMessage(
              mutation.error,
              'Falha ao sincronizar as cobranças.',
            )}
          />
        )}
      </div>
    </Modal>
  );
}

// --- Cancelar / Reverter Debt Arrangement (motivo) ---

export function DebtArrangementReasonModal({
  invalidateKey,
  debtArrangementId,
  action,
  open,
  onClose,
  onSuccess,
}: {
  invalidateKey: readonly unknown[];
  debtArrangementId: string;
  action: 'cancel' | 'revert';
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const [reason, setReason] = useState('');
  const cancelMutation = useCancelDebtArrangement(invalidateKey);
  const revertMutation = useRevertDebtArrangement(invalidateKey);
  const mutation = action === 'cancel' ? cancelMutation : revertMutation;

  useEffect(() => {
    if (open) {
      setReason('');
      cancelMutation.reset();
      revertMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (mutation.isSuccess) {
      onSuccess?.();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutation.isSuccess]);

  const isCancel = action === 'cancel';

  const handleConfirm = () => {
    mutation.mutate({
      debtArrangementId,
      body: { reason: reason || undefined },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isCancel ? 'Cancelar renegociação' : 'Reverter renegociação'}
      description={
        isCancel
          ? 'Cancela um Debt Arrangement ainda em rascunho (DRAFT).'
          : 'Reverte a renegociação (WAITING_PAYMENT/ACTIVE), restaurando as invoices originais. Só sem pagamento confirmado.'
      }
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={mutation.isPending}
          >
            {mutation.isPending
              ? 'Processando…'
              : isCancel
                ? 'Cancelar renegociação'
                : 'Reverter'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {mutation.isError && (
          <ErrorBanner
            message={getApiErrorMessage(
              mutation.error,
              'Falha ao processar a ação.',
            )}
          />
        )}
        <Input
          label="Motivo (opcional)"
          id="da-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Justificativa (vai para o audit log)"
        />
      </div>
    </Modal>
  );
}

// --- Abandonar contrato (override manual → terminal ABANDONED) ---

export function AbandonContractModal({
  invalidateKey,
  contractId,
  open,
  onClose,
}: {
  invalidateKey: readonly unknown[];
  contractId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const mutation = useAbandonContract(invalidateKey);

  useEffect(() => {
    if (open) {
      setReason('');
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (mutation.isSuccess) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutation.isSuccess]);

  const handleConfirm = () => {
    mutation.mutate({ contractId, body: { reason: reason || undefined } });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Abandonar contrato"
      description="Marca o contrato como terminal (ABANDONED) por decisão manual."
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Abandonando…' : 'Abandonar contrato'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {mutation.isError && (
          <ErrorBanner
            message={getApiErrorMessage(
              mutation.error,
              'Falha ao abandonar o contrato.',
            )}
          />
        )}
        <div className="border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-secondary) rounded-lg border p-3 text-xs">
          Override manual: o contrato vai para <strong>ABANDONED</strong> e{' '}
          <strong>não</strong> é mais revertido por eventos do gateway. Os perks
          ativos concedidos sob ele são <strong>revogados</strong>. Ação
          irreversível.
        </div>
        <Input
          label="Motivo (opcional)"
          id="contract-abandon-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Justificativa (vai para o audit log)"
        />
      </div>
    </Modal>
  );
}
